import { Effect, Schema } from 'effect';
import type { HttpClientError } from 'effect/http';
import type { ApiClientError } from './api-client';

/**
 * What the API says about a request it would not take, as far as anything here reads it.
 *
 * A refusal comes back as a 400 with `type`, `message` and `source`, and, when the API is passing on a cause, an `innerException` with the same three again — a `ValidationException` around a `ConstructionNotFoundException`, say, both saying that a profile set was not found. Only the outer message is declared, the way
 * `Account` declares only what it uses: it is the one field anyone is shown, so a change to the rest, the inner exception included, cannot fail the reading of it. Non-empty, since an empty message would be shown as nothing, and the status says more than nothing.
 */
const RefusalBody = Schema.Struct({ message: Schema.NonEmptyString });

const decodeRefusalBody = Schema.decodeUnknownEffect(RefusalBody);

/**
 * What to tell the user about a status that is no answer, when the API gave no reason that could be read.
 *
 * The status and nothing else, because that is all that is known: the body was not there, was not JSON, or carried no message.
 */
function statusMessage(status: number): string {
	return `Serveren svarede med status ${status}.`;
}

/**
 * What to tell the user about a status the API refused a request with, in the API's own words when it sent any.
 *
 * The body of a refusal is read here and nowhere else, which is what keeps every caller from reading it a second way. A body that is not there, is not JSON or carries no message is answered with the status instead, since each of those is the API saying nothing readable about why.
 */
function refusalMessage(reason: HttpClientError.StatusCodeError): Effect.Effect<string> {
	return reason.response.json.pipe(
		Effect.flatMap(decodeRefusalBody),
		Effect.map(({ message }) => message),
		Effect.catch(() => Effect.succeed(statusMessage(reason.response.status))),
	);
}

/**
 * What to tell the user about a request that failed in the exchange itself.
 *
 * A refused status is the one reason the API may have said something about, and it is the only one read. The rest are ours or the browser's: a body that could not be written, a URL that could not be made, a transport that never connected, an answer that could not be read.
 */
function httpFailureMessage({ reason }: HttpClientError.HttpClientError): Effect.Effect<string> {
	switch (reason._tag) {
		case 'StatusCodeError':
			return refusalMessage(reason);
		case 'DecodeError':
		case 'EmptyBodyError':
			return Effect.succeed('Svaret fra serveren kunne ikke læses.');
		case 'EncodeError':
		case 'InvalidUrlError':
		case 'TransportError':
			return Effect.succeed('Forespørgslen kunne ikke sendes.');
	}
}

/**
 * What to tell the user about a call through `ApiClient` that failed, in one sentence.
 *
 * Here rather than beside each caller, because every module speaks to the API through the same client and fails the same ways, and a sentence for each way is worth writing once. In Danish, like everything else a user reads, and plain: what stopped the call, not which class said so. The one exception is a status the
 * API refused the request with, which is answered in the API's own words when it sent any — a value the store does not have is refused with a message that names it, and that message is what the user can act on.
 *
 * An Effect rather than a string, because that one case reads a response body, and that is a read like any other. It cannot fail: a body that cannot be read is answered with the status it came with. Exhaustive over the tags, so a failure added to `ApiClientError` has to be given a sentence here before it compiles.
 */
export function apiFailureMessage(error: ApiClientError): Effect.Effect<string> {
	switch (error._tag) {
		case 'HttpClientError':
			return httpFailureMessage(error);
		case 'NetworkError':
			return Effect.succeed('Serveren kunne ikke nås.');
		case 'AuthenticationError':
			return Effect.succeed('Du er ikke længere logget ind.');
		case 'AuthorizationError':
			return Effect.succeed('Du har ikke adgang til dette.');
		case 'MissingAuthTokenError':
			return Effect.succeed('Du er ikke logget ind.');
		case 'MissingAccountError':
			return Effect.succeed('Der er ikke valgt en konto.');
		case 'MissingDefaultEngineError':
			return Effect.succeed('Kontoen har ingen standardmotor.');
		case 'KeyValueStoreError':
			return Effect.succeed('Browserens lager kunne ikke læses.');
		case 'SchemaError':
			return Effect.succeed('Et svar havde ikke den ventede form.');
	}
}
